import type {
  LinksFunction,
  LoaderFunction,
  MetaFunction,
} from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useLoaderData,
  useRouteError,
  useRouteLoaderData,
} from "@remix-run/react";

import {
  groupedTranslations,
  setCurrentLocaleCode,
  setLocalizationConfigurations,
} from "@mongez/localization";
import { jsxConverter } from "@mongez/react-localization";
import appIcon from "./assets/senaat.ico";
import { useIOSDetection } from "./design-system/hooks";
import { handleRedirects } from "./framework/server";
import { translation } from "./localization/resource";
import stylesheet from "./tailwind.css?url";

// Initialize app on module load
setLocalizationConfigurations({
  converter: jsxConverter,
  defaultLocaleCode: "en",
});

groupedTranslations(translation);

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: stylesheet },
  { rel: "icon", href: appIcon, type: "image/x-icon" },
];

export function ErrorBoundary() {
  const error = useRouteError();

  // Log 5xx and uncaught errors to the remote logging service.
  // Guard with typeof window to avoid double-logging (entry.server already logs server-side).
  // if (typeof window !== "undefined") {
  //   const { message, stack } = normalizeError(error);
  //   const isServerError = isRouteErrorResponse(error) && error.status >= 500;
  //   const isUncaughtError = error instanceof Error;

  //   if (isServerError || isUncaughtError) {
  //     logErrorSafe({
  //       message: isServerError
  //         ? `[${error.status}] ${error.statusText}: ${message}`
  //         : message,
  //       stack,
  //       source: "root.ErrorBoundary",
  //       url: window.location.href,
  //       userAgent: navigator.userAgent,
  //       timestamp: Date.now(),
  //       buildVersion: getBuildVersion(),
  //       errorType: "REACT_RENDER",
  //       statusCode: isRouteErrorResponse(error) ? error.status : 500,
  //       method: "GET",
  //     });
  //   }
  // }

  // Helper function to detect network/API errors
  const isNetworkError = (error: any) => {
    if (error instanceof Error) {
      return (
        error.message.includes("Network Error") ||
        error.message.includes("fetch") ||
        error.message.includes("Failed to fetch") ||
        error.message.includes("ERR_NETWORK") ||
        error.message.includes("ERR_CONNECTION_REFUSED") ||
        error.message.includes("ERR_INTERNET_DISCONNECTED") ||
        (error.name === "TypeError" && error.message.includes("fetch"))
      );
    }
    return false;
  };

  // Helper function to detect API service errors
  const isAPIError = (error: any) => {
    if (isRouteErrorResponse(error)) {
      return (
        error.status >= 500 || error.status === 503 || error.status === 502
      );
    }
    if (error instanceof Error) {
      return (
        error.message.includes("Service Unavailable") ||
        error.message.includes("Internal Server Error")
      );
    }
    return false;
  };

  // Handle network connectivity issues
  if (isNetworkError(error)) {
    return (
      <div
        dir="ltr"
        className="flex min-h-screen items-center justify-center bg-gray-50"
      >
        <div className="w-full max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
          <div className="mb-6">
            <svg
              className="mx-auto h-16 w-16 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h1 className="mb-4 text-2xl font-bold text-gray-900">
            Connection Error
          </h1>
          <p className="mb-6 text-gray-600">
            We're having trouble connecting to our servers. Please check your
            internet connection and try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Handle API service errors
  if (isAPIError(error)) {
    return (
      <div
        dir="ltr"
        className="flex min-h-screen items-center justify-center bg-gray-50"
      >
        <div className="w-full max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
          <div className="mb-6">
            <svg
              className="mx-auto h-16 w-16 text-orange-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="mb-4 text-2xl font-bold text-gray-900">
            Service Temporarily Unavailable
          </h1>
          <p className="mb-6 text-gray-600">
            Our servers are currently experiencing issues. We're working to
            resolve this as quickly as possible.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
            >
              Retry
            </button>
            <button
              onClick={() => window.history.back()}
              className="w-full rounded-lg bg-gray-200 px-4 py-2 font-medium text-gray-800 transition-colors hover:bg-gray-300"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Handle route errors (404, 401, etc.)
  if (isRouteErrorResponse(error)) {
    const getErrorMessage = (status: number) => {
      switch (status) {
        case 404:
          return "The page you're looking for doesn't exist.";
        case 401:
          return "You need to be logged in to access this page.";
        case 403:
          return "You don't have permission to access this page.";
        case 500:
          return "Something went wrong on our end. Please try again later.";
        default:
          return error.data || "An error occurred while loading this page.";
      }
    };

    return (
      <div
        dir="ltr"
        className="flex min-h-screen items-center justify-center bg-gray-50"
      >
        <div className="w-full max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
          <div className="mb-6">
            <div className="mb-2 text-6xl font-bold text-gray-300">
              {error.status}
            </div>
            <div className="text-sm text-gray-500">{error.statusText}</div>
          </div>
          <h1 className="mb-4 text-2xl font-bold text-gray-900">
            {error.status === 404 ? "Page Not Found" : "Error"}
          </h1>
          <p className="mb-6 text-gray-600">{getErrorMessage(error.status)}</p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
            >
              Try Again
            </button>
            <button
              onClick={() => window.history.back()}
              className="w-full rounded-lg bg-gray-200 px-4 py-2 font-medium text-gray-800 transition-colors hover:bg-gray-300"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Handle general JavaScript errors
  if (error instanceof Error) {
    // Log error for debugging (only in development)
    if (process.env.NODE_ENV === "development") {
      console.error("Error Boundary caught an error:", error);
    }

    return (
      <div
        dir="ltr"
        className="flex min-h-screen items-center justify-center bg-gray-50"
      >
        <div className="w-full max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
          <div className="mb-6">
            <svg
              className="mx-auto h-16 w-16 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h1 className="mb-4 text-2xl font-bold text-gray-900">
            Something Went Wrong
          </h1>
          <p className="mb-6 text-gray-600">
            We encountered an unexpected error. Our team has been notified and
            is working to fix it.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
            >
              Reload Page
            </button>
            <button
              onClick={() => window.history.back()}
              className="w-full rounded-lg bg-gray-200 px-4 py-2 font-medium text-gray-800 transition-colors hover:bg-gray-300"
            >
              Go Back
            </button>
          </div>
          {process.env.NODE_ENV === "development" && (
            <details className="mt-6 text-left">
              <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                Error Details (Development)
              </summary>
              <div className="mt-2 max-h-32 overflow-auto rounded bg-gray-100 p-3 font-mono text-xs text-gray-800">
                <div className="mb-1 font-semibold">Message:</div>
                <div className="mb-2">{error.message}</div>
                {error.stack && (
                  <>
                    <div className="mb-1 font-semibold">Stack:</div>
                    <div>{error.stack}</div>
                  </>
                )}
              </div>
            </details>
          )}
        </div>
      </div>
    );
  }

  // Handle unknown errors
  return (
    <div
      dir="ltr"
      className="flex min-h-screen items-center justify-center bg-gray-50"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
        <div className="mb-6">
          <svg
            className="mx-auto h-16 w-16 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h1 className="mb-4 text-2xl font-bold text-gray-900">Unknown Error</h1>
        <p className="mb-6 text-gray-600">
          An unexpected error occurred. Please try refreshing the page.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
        >
          Refresh Page
        </button>
      </div>
    </div>
  );
}

export const meta: MetaFunction = () => {
  return [
    { title: "SENAAT" },
    {
      name: "description",
      content: "",
    },
  ];
};

export const loader: LoaderFunction = async ({ request, params }) => {
  const result = handleRedirects({ request, params });

  if (result instanceof Response) {
    return result;
  }

  const { lang } = result;

  return { lang };
};

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData<{ lang: string }>("root") ?? { lang: "en" };

  return (
    <html lang={data.lang} dir={data.lang === "ar" ? "rtl" : "ltr"}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <script />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              // Initialize Google Analytics with consent
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              
              // Set default consent to denied
              gtag('consent', 'default', {
                'analytics_storage': 'denied',
                'ad_storage': 'denied',
                'functionality_storage': 'denied',
                'personalization_storage': 'denied',
                'security_storage': 'granted'
              });
              
              // Check for existing consent
              const savedConsent = localStorage.getItem('cookie_consent');
              if (savedConsent) {
                try {
                  const consent = JSON.parse(savedConsent);
                  if (consent.analytics) {
                    gtag('consent', 'update', {
                      'analytics_storage': 'granted'
                    });
                  }
                  if (consent.marketing) {
                    gtag('consent', 'update', {
                      'ad_storage': 'granted',
                      'personalization_storage': 'granted'
                    });
                  }
                  if (consent.functional) {
                    gtag('consent', 'update', {
                      'functionality_storage': 'granted'
                    });
                  }
                } catch (error) {
                  console.error('Error parsing cookie consent:', error);
                }
              }
              
              // Load Google Analytics script only if analytics consent is granted
              function loadGoogleAnalytics() {
                const script = document.createElement('script');
                script.async = true;
                script.src = 'https://www.googletagmanager.com/gtag/js?id=G-M50MW7620N';
                document.head.appendChild(script);
                
                gtag('config', 'G-M50MW7620N');
              }
              
              // Load analytics if consent is already granted
              if (savedConsent) {
                try {
                  const consent = JSON.parse(savedConsent);
                  if (consent.analytics) {
                    loadGoogleAnalytics();
                  }
                } catch (error) {
                  console.error('Error parsing cookie consent:', error);
                }
              }
              
              // Make loadGoogleAnalytics available globally for consent updates
              window.loadGoogleAnalytics = loadGoogleAnalytics;
            `,
          }}
        />
      </head>
      <body className="ltr:font-acumin-variable rtl:font-ibm-plex-arabic">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

type AppLoaderProps = {
  lang: string;
};

export default function App() {
  const { lang } = useLoaderData<AppLoaderProps>();

  // Initialize iOS detection and store in atom
  useIOSDetection();

  setCurrentLocaleCode(lang);

  return <Outlet />;
}
